import { useState, useEffect, useRef, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../api/api";
import { AuthContext } from "../context/AuthContext";

const SERVICE_LABELS = {
  aire_acondicionado: "Aire Acondicionado", albanileria: "Albañilería",
  arquitectura: "Arquitectura", belleza_personal: "Belleza Personal",
  carpinteria: "Carpintería", cerrajeria: "Cerrajería",
  control_plagas: "Control de Plagas", cuidado_mayores: "Cuidado de Mayores",
  cuidado_ninos: "Cuidado de Niños", decoradores: "Decoradores",
  electricista: "Electricista", entrenamiento: "Entrenamiento",
  gasista: "Gasista", herrero: "Herrero", infantiles: "Infantiles",
  instalador_general: "Instalador General", jardinero: "Jardinero",
  mascotas: "Mascotas", mecanica_automotriz: "Mecánica Automotriz",
  mudanzas: "Mudanzas", piletas: "Piletas", pintores: "Pintores",
  plomeros: "Plomeros", seguridad: "Seguridad",
  servicio_domestico: "Servicio Doméstico", tapicero: "Tapicero",
  tecnologia: "Tecnología",
};

function formatTime(dateStr) {
  const d = new Date(dateStr);
  const today = new Date();
  const isToday =
    d.getDate() === today.getDate() &&
    d.getMonth() === today.getMonth() &&
    d.getFullYear() === today.getFullYear();

  if (isToday) {
    return d.toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" });
  }
  return d.toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit" }) +
    " " + d.toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" });
}

function groupMessagesByDate(messages) {
  const groups = [];
  let lastDate = null;

  messages.forEach(msg => {
    const d = new Date(msg.created_at);
    const dateKey = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
    if (dateKey !== lastDate) {
      lastDate = dateKey;
      const today = new Date();
      const isToday =
        d.getDate() === today.getDate() &&
        d.getMonth() === today.getMonth() &&
        d.getFullYear() === today.getFullYear();
      const yesterday = new Date(today);
      yesterday.setDate(today.getDate() - 1);
      const isYesterday =
        d.getDate() === yesterday.getDate() &&
        d.getMonth() === yesterday.getMonth() &&
        d.getFullYear() === yesterday.getFullYear();

      groups.push({
        type: "date",
        label: isToday ? "Hoy" : isYesterday ? "Ayer" :
          d.toLocaleDateString("es-AR", { day: "2-digit", month: "long", year: "numeric" }),
      });
    }
    groups.push({ type: "message", ...msg });
  });

  return groups;
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function ChatPage() {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  const [chatData,    setChatData]    = useState(null);
  const [messages,    setMessages]    = useState([]);
  const [inputText,   setInputText]   = useState("");
  const [loadingData, setLoadingData] = useState(true);
  const [loadError,   setLoadError]   = useState("");
  const [sending,     setSending]     = useState(false);

  const messagesEndRef = useRef(null);
  const inputRef       = useRef(null);
  const pollingRef     = useRef(null);

  // ─── Scroll al último mensaje ─────────────────────────────────────────────
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // ─── Cargar chat inicial ──────────────────────────────────────────────────
  useEffect(() => {
    if (!requestId) return;
    setLoadingData(true);
    api.get(`/chat/${requestId}`)
      .then(res => {
        setChatData(res.data.request);
        setMessages(res.data.messages || []);
        // Marcar como leidos al abrir
        api.patch(`/chat/${requestId}/read`).catch(() => {});
      })
      .catch(err => {
        const status = err.response?.status;
        if (status === 403) setLoadError("No tenés acceso a este chat.");
        else if (status === 400) setLoadError("El chat no está disponible para esta solicitud.");
        else setLoadError("No se pudo cargar el chat.");
      })
      .finally(() => setLoadingData(false));
  }, [requestId]);

  // ─── Scroll cuando llegan mensajes nuevos ────────────────────────────────
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // ─── Polling cada 5 segundos ──────────────────────────────────────────────
  useEffect(() => {
    if (!chatData) return;

    pollingRef.current = setInterval(() => {
      api.get(`/chat/${requestId}`)
        .then(res => {
          const newMessages = res.data.messages || [];
          setMessages(prev => {
            if (newMessages.length !== prev.length) {
              api.patch(`/chat/${requestId}/read`).catch(() => {});
              return newMessages;
            }
            return prev;
          });
        })
        .catch(() => {});
    }, 5000);

    return () => clearInterval(pollingRef.current);
  }, [chatData, requestId]);

  // ─── Enviar mensaje ───────────────────────────────────────────────────────
  const handleSend = async () => {
    const body = inputText.trim();
    if (!body || sending) return;

    setSending(true);
    setInputText("");

    try {
      const res = await api.post(`/chat/${requestId}`, { body });
      setMessages(prev => [...prev, res.data.message]);
    } catch (err) {
      setInputText(body); // restaurar si falla
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // ─── Loading ──────────────────────────────────────────────────────────────
  if (loadingData) return (
    <div style={{
      minHeight: "60vh", display: "flex", alignItems: "center",
      justifyContent: "center", fontFamily: "Montserrat, sans-serif",
    }}>
      <div style={{ textAlign: "center", color: "#8fa3b1" }}>
        <span style={{
          display: "inline-block", width: 28, height: 28,
          border: "3px solid #cddae4", borderTopColor: "#163244",
          borderRadius: "50%", animation: "spin 0.7s linear infinite",
          marginBottom: 12,
        }} />
        <div style={{ fontWeight: 600 }}>Cargando chat...</div>
        <style>{"@keyframes spin { to { transform: rotate(360deg); } }"}</style>
      </div>
    </div>
  );

  // ─── Error ────────────────────────────────────────────────────────────────
  if (loadError) return (
    <div style={{
      minHeight: "60vh", display: "flex", alignItems: "center",
      justifyContent: "center", fontFamily: "Montserrat, sans-serif",
    }}>
      <div style={{ textAlign: "center" }}>
        <div style={{ fontSize: 36, marginBottom: 12 }}>⚠️</div>
        <div style={{ fontWeight: 600, color: "#c0392b", marginBottom: 16 }}>{loadError}</div>
        <button
          onClick={() => navigate(-1)}
          style={{
            padding: "10px 24px", borderRadius: 8, background: "#163244",
            color: "#fff", border: "none", fontFamily: "Montserrat, sans-serif",
            fontWeight: 600, cursor: "pointer",
          }}
        >
          ← Volver
        </button>
      </div>
    </div>
  );

  const isClient        = user?.id === chatData?.client_id;
  const otherName       = isClient ? chatData?.provider_name : chatData?.client_name;
  const serviceLabel    = SERVICE_LABELS[chatData?.service_key] || chatData?.service_key;
  const grouped         = groupMessagesByDate(messages);
  const isCompleted     = chatData?.status === "completed";

  return (
    <div style={{
      fontFamily: "Montserrat, sans-serif",
      maxWidth: 720, margin: "0 auto",
      padding: "24px 20px 40px",
      display: "flex", flexDirection: "column",
      height: "calc(100vh - 140px)",
    }}>

      {/* ── Header ── */}
      <div style={{
        background: "#fff", borderRadius: 14, padding: "16px 20px",
        marginBottom: 12, boxShadow: "0 2px 10px rgba(22,50,68,0.06)",
        border: "1px solid #e0e9ef",
        display: "flex", alignItems: "center", gap: 14,
      }}>
        <button
          onClick={() => navigate(-1)}
          style={{
            background: "none", border: "none", cursor: "pointer",
            color: "#163244", fontSize: 18, padding: 0, flexShrink: 0,
          }}
        >
          ←
        </button>

        {/* Avatar */}
        <div style={{
          width: 44, height: 44, borderRadius: "50%", flexShrink: 0,
          background: "#163244", display: "flex", alignItems: "center",
          justifyContent: "center", fontWeight: 800, fontSize: 18, color: "#E7A432",
        }}>
          {otherName?.[0] || "?"}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 800, fontSize: 15, color: "#163244" }}>
            {otherName}
          </div>
          <div style={{ fontSize: 12, color: "#8fa3b1", marginTop: 2 }}>
            {serviceLabel} · Solicitud #{requestId}
          </div>
        </div>

        {/* Badge estado */}
        <span style={{
          padding: "4px 12px", borderRadius: 20, fontSize: 11, fontWeight: 700,
          color: isCompleted ? "#163244" : "#1b7f4e",
          background: isCompleted ? "#e7f0f5" : "#e8f7ef",
          whiteSpace: "nowrap", flexShrink: 0,
        }}>
          {isCompleted ? "Completado" : "En curso"}
        </span>
      </div>

      {/* ── Mensajes ── */}
      <div style={{
        flex: 1, overflowY: "auto",
        background: "#f0f5f8", borderRadius: 14,
        padding: "20px 16px", marginBottom: 12,
        display: "flex", flexDirection: "column", gap: 4,
      }}>

        {messages.length === 0 && (
          <div style={{
            flex: 1, display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center",
            color: "#8fa3b1", textAlign: "center", padding: "40px 20px",
          }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>💬</div>
            <div style={{ fontWeight: 700, fontSize: 15, color: "#163244", marginBottom: 6 }}>
              Iniciá la conversación
            </div>
            <div style={{ fontSize: 13 }}>
              Coordiná los detalles del trabajo con {otherName}.
            </div>
          </div>
        )}

        {grouped.map((item, i) => {
          if (item.type === "date") return (
            <div key={`date-${i}`} style={{
              textAlign: "center", margin: "12px 0 4px",
              fontSize: 11, color: "#8fa3b1", fontWeight: 600,
            }}>
              <span style={{
                background: "#e0e9ef", borderRadius: 10,
                padding: "3px 12px",
              }}>
                {item.label}
              </span>
            </div>
          );

          const isMine = item.sender_id === user?.id;

          return (
            <div
              key={item.id}
              style={{
                display: "flex",
                justifyContent: isMine ? "flex-end" : "flex-start",
                marginBottom: 2,
              }}
            >
              <div style={{
                maxWidth: "72%",
                background: isMine ? "#163244" : "#fff",
                color: isMine ? "#fff" : "#163244",
                borderRadius: isMine
                  ? "14px 14px 4px 14px"
                  : "14px 14px 14px 4px",
                padding: "10px 14px",
                boxShadow: "0 1px 4px rgba(22,50,68,0.08)",
              }}>
                <div style={{ fontSize: 14, lineHeight: 1.5, wordBreak: "break-word" }}>
                  {item.body}
                </div>
                <div style={{
                  fontSize: 10, marginTop: 4, textAlign: "right",
                  color: isMine ? "rgba(255,255,255,0.55)" : "#aab8c2",
                  display: "flex", alignItems: "center",
                  justifyContent: "flex-end", gap: 4,
                }}>
                  {formatTime(item.created_at)}
                  {isMine && (
                    <span style={{ fontSize: 11 }}>
                      {item.read ? "✓✓" : "✓"}
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}

        <div ref={messagesEndRef} />
      </div>

      {/* ── Input ── */}
      {isCompleted ? (
        <div style={{
          background: "#f7fafc", borderRadius: 12, padding: "14px 20px",
          textAlign: "center", color: "#8fa3b1", fontSize: 13,
          border: "1px solid #e0e9ef",
        }}>
          Este trabajo fue completado. El chat está en modo solo lectura.
        </div>
      ) : (
        <div style={{
          background: "#fff", borderRadius: 14, padding: "10px 12px",
          boxShadow: "0 2px 10px rgba(22,50,68,0.06)",
          border: "1px solid #e0e9ef",
          display: "flex", alignItems: "flex-end", gap: 10,
        }}>
          <textarea
            ref={inputRef}
            value={inputText}
            onChange={e => setInputText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={`Escribí un mensaje a ${otherName}...`}
            rows={1}
            style={{
              flex: 1, border: "none", outline: "none", resize: "none",
              fontFamily: "Montserrat, sans-serif", fontSize: 14,
              color: "#163244", background: "transparent",
              lineHeight: 1.5, maxHeight: 120, overflowY: "auto",
              padding: "6px 4px",
            }}
            onInput={e => {
              e.target.style.height = "auto";
              e.target.style.height = Math.min(e.target.scrollHeight, 120) + "px";
            }}
          />
          <button
            onClick={handleSend}
            disabled={!inputText.trim() || sending}
            style={{
              width: 40, height: 40, borderRadius: "50%", border: "none",
              background: !inputText.trim() || sending ? "#e0e9ef" : "#163244",
              color: !inputText.trim() || sending ? "#aab8c2" : "#E7A432",
              cursor: !inputText.trim() || sending ? "not-allowed" : "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 18, flexShrink: 0, transition: "all 0.2s",
            }}
          >
            {sending ? (
              <span style={{
                width: 14, height: 14, border: "2px solid #aab8c2",
                borderTopColor: "transparent", borderRadius: "50%",
                animation: "spin 0.7s linear infinite", display: "inline-block",
              }} />
            ) : "↑"}
          </button>
        </div>
      )}

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800&display=swap');
        * { box-sizing: border-box; }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
